<script>
import StatsGameService from "@/services/statService.js";
import DiceNRolls from "@/components/Dice/DiceNRolls.vue";
import SwapIcon from "@/components/icons/Swap.vue";

export default {
  name: "RPGStatPresentor",
  components: {SwipeIcon: SwapIcon, DiceNRolls},
  props: {
    allowResetBaseStats: {
      type: Boolean,
      required: false,
    },
    stat: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false, // Enable or disable value editing
    },
  },
  data() {
    return {
      statsService: StatsGameService,
    };
  },
  computed: {
    // Calculate the base value from dice rolls
    baseValue() {
      if (!this.stat.dice_rolls || this.stat.dice_rolls.length === 0) {
        return this.stat.base_value || 0;
      }
      // Use the same calculation as DiceNRolls component
      const fadeCount = 1; // Assuming 1 dice is faded out
      const sorted = [...this.stat.dice_rolls.map(d => d.dice_side)].sort((a, b) => a - b);
      return sorted.slice(fadeCount).reduce((sum, roll) => sum + roll, 0);
    },

    // Calculate the total value (base + modifier)
    totalValue() {
      return this.baseValue + (this.stat.additional_value || 0);
    },

    // Get modifier value
    modifierValue() {
      return this.stat.additional_value || 0;
    },

    // Format the modifier with appropriate color class
    modifierText() {
      const additional = this.stat.additional_value || 0;
      if (additional === 0) {
        return '';
      }
      return additional > 0 ? `+${additional}` : `${additional}`;
    },

    // Get modifier color class
    modifierColorClass() {
      const additional = this.stat.additional_value || 0;
      if (additional > 0) return 'modifier-positive';
      if (additional < 0) return 'modifier-negative';
      return '';
    }
  },
};
</script>

<template>
  <div class="stat-presenter" :class="{ 'is-modified': modifierValue !== 0 }">
    <img
        class="stat-icon"
        :src="statsService.getCachedStatImage(stat.name)"
        :alt="stat.name"
        :title="stat.name"
    />
    <span class="stat-tick" aria-hidden="true"></span>

    <div class="left-section">
      <span class="stat-name">{{ stat.name }}</span>
    </div>

    <div class="right-section">
      <DiceNRolls :dice-rolls="stat.dice_rolls" :stat="stat.name" class="dice-results"/>
      <span v-if="modifierText" class="modifier" :class="modifierColorClass">{{ modifierText }}</span>
      <span class="stat-total" :class="{ 'is-plain': modifierValue === 0 }">{{ totalValue }}</span>
      <SwipeIcon @click="$emit('switchBaseStats', stat.id)" class="swap-icon" v-if="allowResetBaseStats"/>
    </div>
  </div>
</template>

<style scoped>
.stat-presenter {
  --hud-cyan: #3ee9f0;
  --hud-line: rgba(0, 255, 255, 0.18);
  --hud-slab: rgba(11, 18, 24, 0.72);
  --hud-text: #d3e0e6;
  --hud-text-dim: #7f929c;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  overflow: hidden;
  padding: 0.3rem 0.5rem 0.3rem 0.6rem;
  background: var(--hud-slab);
  border: 1px solid var(--hud-line);
  border-radius: 0.2rem;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.stat-presenter:hover {
  background: rgba(16, 26, 34, 0.88);
  border-color: rgba(62, 233, 240, 0.34);
}

/* Accent tick on the leading edge - the marker the tactical HUD uses for an active row. */
.stat-tick {
  position: absolute;
  top: 0.35rem;
  bottom: 0.35rem;
  left: 0;
  width: 2px;
  background: rgba(62, 233, 240, 0.28);
  transition: background-color 0.15s ease;
}

.stat-presenter:hover .stat-tick,
.stat-presenter.is-modified .stat-tick {
  background: var(--hud-cyan);
}

.left-section {
  display: flex;
  z-index: 1;
  flex: 1;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.right-section {
  display: flex;
  z-index: 1;
  align-items: center;
  gap: 0.45rem;
  margin-left: auto;
}

.stat-icon {
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  height: 100%;
  width: 46%;
  object-fit: cover;
  opacity: 0.3;
  mask: linear-gradient(to left, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0) 85%);
  filter: saturate(0.35) brightness(0.85);
  transition: opacity 0.15s ease;
}

.stat-presenter:hover .stat-icon {
  opacity: 0.45;
}

.stat-name {
  overflow: hidden;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--hud-text);
  text-shadow: 0 1px 2px rgba(0, 10, 14, 0.95);
}

/* The dice keep their expand-on-click behaviour; only the plating changes to match the HUD. */
.dice-results :deep(.dice) {
  color: #cfe9ee;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  background: linear-gradient(135deg, #16323b, #0c1a20);
  border: 1px solid rgba(62, 233, 240, 0.4);
}

.dice-results :deep(.dice.faded) {
  color: #5d7076 !important;
  background: linear-gradient(135deg, #131b20, #0a1014) !important;
  border: 1px solid rgba(125, 146, 156, 0.35);
}

.dice-results :deep(.dice.summary) {
  color: var(--hud-cyan);
  background: linear-gradient(135deg, #12262d, #08131a);
  border: 1px solid rgba(62, 233, 240, 0.55);
}

.modifier {
  padding: 0.05rem 0.22rem;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 0.62rem;
  font-weight: 600;
  border: 1px solid;
  border-radius: 0.15rem;
}

.modifier-positive {
  color: #7fd6a2;
  background: rgba(127, 214, 162, 0.1);
  border-color: rgba(127, 214, 162, 0.35);
}

.modifier-negative {
  color: #e88a84;
  background: rgba(232, 138, 132, 0.1);
  border-color: rgba(232, 138, 132, 0.35);
}

/* The total is what the player actually rolls against, so it reads loudest. */
.stat-total {
  min-width: 1.9rem;
  padding: 0.1rem 0.3rem;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: center;
  color: #f2c489;
  background: rgba(6, 10, 14, 0.72);
  border: 1px solid rgba(242, 196, 137, 0.28);
  border-radius: 0.15rem;
  text-shadow: 0 1px 2px rgba(0, 10, 14, 0.95);
}

/* With no modifier the total repeats the dice sum, so the plate holds its column
   without printing the same number twice. */
.stat-total.is-plain {
  visibility: hidden;
}

.swap-icon {
  color: rgba(62, 233, 240, 0.6);
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
}

.swap-icon:hover {
  color: var(--hud-cyan);
  transform: scale(1.12);
}
</style>
